import type { FieldValues } from 'react-hook-form';
import { useFormContext } from 'react-hook-form';

import { Calendar } from '@/components/ui/calendar';
import { DatePicker } from '@/components/ui/date-picker';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';

import type { FieldBaseProps } from './types';

type Props<T extends FieldValues> = FieldBaseProps<T> & {
  placeholder?: string;
  /** react-day-picker matcher for non-selectable days. */
  disabledDates?: React.ComponentProps<typeof Calendar>['disabled'];
  /** Pass true when used inside a Radix Dialog (see DatePicker). */
  modal?: boolean;
};

export function FormDateField<T extends FieldValues>({
  name,
  label,
  description,
  required,
  disabled,
  className,
  placeholder,
  disabledDates,
  modal,
}: Props<T>) {
  const { control } = useFormContext<T>();

  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem className={className}>
          {label ? (
            <FormLabel>
              {label}
              {required ? <span aria-hidden="true"> *</span> : null}
            </FormLabel>
          ) : null}
          <FormControl>
            <DatePicker
              value={field.value as string | undefined}
              onChange={field.onChange}
              placeholder={placeholder}
              disabled={disabled}
              disabledDates={disabledDates}
              modal={modal}
            />
          </FormControl>
          {description ? <FormDescription>{description}</FormDescription> : null}
          <FormMessage />
        </FormItem>
      )}
    />
  );
}
